import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Spinner } from './icons';

export type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

/**
 * Inline saving (docs/10, design/Zustaende "Inline speichern"): saves `value` `delay` ms after the last
 * change, one request at a time (a change during a save is saved right after). "saved" falls back to
 * idle after 3 s. `isEqual` decides whether something changed (default: JSON compare). A change still waiting for
 * its delay is saved right away when the component unmounts.
 */
export function useAutosave<T>(value: T, save: (v: T) => Promise<unknown>, { delay = 800, enabled = true, isEqual = (a: T, b: T) => JSON.stringify(a) === JSON.stringify(b) }: { delay?: number; enabled?: boolean; isEqual?: (a: T, b: T) => boolean } = {}) {
  const [state, setState] = useState<SaveState>('idle');
  const [error, setError] = useState<unknown>(null);
  const saved = useRef(value);
  const latest = useRef(value);
  const saveRef = useRef(save);
  const busy = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const fade = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  latest.current = value;
  saveRef.current = save;

  const run = useCallback(async () => {
    clearTimeout(timer.current);
    timer.current = undefined;
    if (busy.current || isEqual(latest.current, saved.current)) return;
    busy.current = true;
    const v = latest.current;
    setState('saving');
    try {
      await saveRef.current(v);
      saved.current = v;
      setError(null);
      busy.current = false;
      if (!isEqual(latest.current, saved.current)) return void run();
      setState('saved');
      clearTimeout(fade.current);
      fade.current = setTimeout(() => setState((s) => (s === 'saved' ? 'idle' : s)), 3000);
    } catch (e) {
      busy.current = false;
      setError(e);
      setState('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!enabled || isEqual(value, saved.current)) return;
    setState((s) => (s === 'saving' ? s : 'dirty'));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => void run(), delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, enabled, delay]);
  // leaving the page (or a tab of it) inside the debounce window still saves the last change
  useEffect(() => () => { clearTimeout(fade.current); if (timer.current !== undefined) void run(); }, [run]);

  /** Treat the given value as saved (e.g. after the server returned a normalised version). */
  const reset = useCallback((v: T) => { saved.current = v; setState('idle'); setError(null); }, []);
  return { state, error, flush: run, reset };
}

/** Status next to an inline-saved field: "Speichert …", "Gespeichert", "geändert", "Nicht gespeichert". */
export function SaveStatus({ state, onRetry }: { state: SaveState; onRetry?: () => void }) {
  const { t } = useTranslation('common');
  return (
    <span className="save-status" role="status" aria-live="polite">
      {state === 'saving' && <span className="row" style={{ gap: 6 }}><Spinner size={14} />{t('ui.save.saving')}</span>}
      {state === 'saved' && <span className="saved"><Icon name="check" size={14} strokeWidth={2.4} />{t('ui.save.saved')}</span>}
      {state === 'dirty' && <span>{t('ui.save.dirty')}</span>}
      {state === 'error' && (
        <span className="failed">
          {t('ui.save.error')}
          {onRetry && <> · <button type="button" className="link-button" onClick={onRetry}>{t('retry')}</button></>}
        </span>
      )}
    </span>
  );
}
